import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Cpu, Loader2, Mail, ShieldAlert } from "lucide-react";
import { AdminPage } from "@/components/AdminPage";
import { api } from "../lib/api";
import { mapAdminApiError } from "../lib/errors";
import type { ApiContract } from "@podecontratar/shared/contract/index";
import type { EndpointOutput } from "@podecontratar/shared/contract/endpoint";

type Metrics = EndpointOutput<ApiContract["GET /admin/technical/metrics"]>;
type VerifRow = EndpointOutput<ApiContract["GET /admin/technical/verifications"]>[number];

const WINDOWS = [
  { value: 1, label: "24 h" },
  { value: 7, label: "7 dias" },
  { value: 30, label: "30 dias" },
  { value: 90, label: "90 dias" },
];

const STATUS_LABEL: Record<string, string> = {
  pending: "Pendente",
  paid: "Pago",
  processing: "Processando",
  completed: "Concluída",
  failed: "Falhou",
  refunded: "Reembolsada",
  expired: "Expirada",
};

const BLACKLIST_REASON_LABEL: Record<string, string> = {
  hard_bounce: "Bounce permanente",
  soft_bounce: "Bounce temporário",
  spam_report: "Marcado como spam",
  unsubscribe: "Descadastrou",
  low_engagement: "Sem engajamento",
  manual: "Manual",
};

function formatPct(n: number | null): string {
  return n == null ? "—" : `${n.toFixed(1)}%`;
}

function formatDuration(s: number | null): string {
  if (s == null) return "—";
  if (s < 60) return `${s.toFixed(1)} s`;
  if (s < 3600) return `${(s / 60).toFixed(1)} min`;
  return `${(s / 3600).toFixed(2)} h`;
}

function formatBRL(brl: number): string {
  // 4 decimals: AI costs per consulta land in the sub-centavo range
  // (~R$ 0,0012). Standard 2-digit formatting renders them as "R$ 0,00",
  // which hides the signal we're trying to surface for pricing analysis.
  return brl.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
}

function formatDateTime(iso: string): string {
  return new Date(iso.replace(" ", "T") + "Z").toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AdminTechnicalPage() {
  const [days, setDays] = useState(30);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [verifs, setVerifs] = useState<VerifRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    Promise.all([
      api.request("GET /admin/technical/metrics", { query: { days } }),
      api.request("GET /admin/technical/verifications", { query: { days, limit: 50 } }),
    ])
      .then(([m, v]) => {
        if (!alive) return;
        setMetrics(m);
        setVerifs(v.items);
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        setError(mapAdminApiError(err));
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [days]);

  const statusTotal = useMemo(
    () => (metrics?.status_mix ?? []).reduce((acc, r) => acc + r.n, 0),
    [metrics]
  );

  const windowSwitcher = (
    <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface p-1">
      {WINDOWS.map((w) => (
        <button
          key={w.value}
          type="button"
          onClick={() => setDays(w.value)}
          className={`rounded-md px-3 py-1.5 text-xs font-medium tracking-tight transition-colors ${
            days === w.value
              ? "bg-surface-alt text-text"
              : "text-text-tertiary hover:text-text"
          }`}
        >
          {w.label}
        </button>
      ))}
    </div>
  );

  return (
    <AdminPage
      eyebrow="Operacional"
      title="Técnico"
      description="Tokens, tempo de verificação, custo de IA e operação geral."
      action={windowSwitcher}
    >
      {error && (
        <div className="mb-6 rounded-2xl border border-danger/30 bg-danger/10 p-4 text-sm text-text">
          {error}
        </div>
      )}

      {loading ? (
        <div className="grid place-items-center py-16">
          <Loader2 size={20} className="animate-spin text-text-tertiary" aria-hidden />
        </div>
      ) : metrics ? (
        <div className="flex flex-col gap-12">
          {/* ───────── IA ───────── */}
          <div>
            <SectionHeader
              icon={Cpu}
              title="IA"
              subtitle="Custos, tempo de verificação e cache."
            />
            <section className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat
                label="Custo de IA"
                value={
                  metrics.ai.totals ? formatBRL(metrics.ai.totals.cost_brl) : "—"
                }
                hint={
                  metrics.ai.totals
                    ? `${metrics.ai.totals.calls} chamadas · ${metrics.ai.totals.total_tokens.toLocaleString("pt-BR")} tokens`
                    : "Sem dados"
                }
              />
              <Stat
                label="Custo médio por consulta"
                value={
                  metrics.ai.totals
                    ? formatBRL(metrics.ai.totals.avg_cost_per_verification_brl)
                    : "—"
                }
                hint={
                  metrics.ai.totals
                    ? `Base: ${metrics.ai.totals.verifications} consultas com IA`
                    : "Sem dados"
                }
              />
              <Stat
                label="Tempo médio (verificação)"
                value={formatDuration(metrics.timing.avg_seconds)}
                hint={`p50 ${formatDuration(metrics.timing.p50_seconds)} · p95 ${formatDuration(metrics.timing.p95_seconds)}`}
              />
              <Stat
                label="Cache hit rate"
                value={
                  metrics.cache.cache_hit_rate_pct != null
                    ? `${metrics.cache.cache_hit_rate_pct.toFixed(1)}%`
                    : "—"
                }
                hint={`${metrics.cache.cache_hits}/${metrics.cache.completed_in_window} consultas`}
              />
            </section>
          </div>

          {/* ───────── E-MAILS ───────── */}
          <div>
            <SectionHeader
              icon={Mail}
              title="E-mails"
              subtitle="Funil de entregabilidade e blacklist."
            />
            <section className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat
                label="Enviados"
                value={metrics.emails.totals.sent.toLocaleString("pt-BR")}
                hint={`${metrics.emails.totals.delivered.toLocaleString("pt-BR")} entregues`}
              />
              <Stat
                label="Taxa de entrega"
                value={formatPct(metrics.emails.rates.delivery_rate_pct)}
                hint={`${metrics.emails.totals.bounced.toLocaleString("pt-BR")} bounces · ${metrics.emails.totals.deferred.toLocaleString("pt-BR")} adiados`}
              />
              <Stat
                label="Taxa de abertura"
                value={formatPct(metrics.emails.rates.open_rate_pct)}
                hint={`${metrics.emails.totals.opened.toLocaleString("pt-BR")} abertos · clique ${formatPct(metrics.emails.rates.click_rate_pct)}`}
              />
              <Stat
                label="Blacklist ativa"
                value={metrics.emails.blacklist.active.toLocaleString("pt-BR")}
                hint={
                  metrics.emails.rates.bounce_rate_pct != null
                    ? `${formatPct(metrics.emails.rates.bounce_rate_pct)} bounce rate`
                    : "Endereços bloqueados"
                }
              />
            </section>

            <section className="mt-6 grid gap-6 lg:grid-cols-2">
              <Card title="Por template">
                {metrics.emails.by_template.length === 0 ? (
                  <p className="text-sm text-text-tertiary">Sem envios no período.</p>
                ) : (
                  <ul className="flex flex-col gap-3">
                    {metrics.emails.by_template.map((r) => {
                      const openPct =
                        r.delivered > 0
                          ? (r.opened / r.delivered) * 100
                          : null;
                      return (
                        <li
                          key={r.template}
                          className="flex items-baseline justify-between gap-3 text-sm"
                        >
                          <span className="truncate text-text">{r.template}</span>
                          <div className="text-right">
                            <p className="font-medium text-text">
                              {r.sent.toLocaleString("pt-BR")} envios
                            </p>
                            <p className="text-xs text-text-tertiary">
                              {r.opened} abertos
                              {openPct != null && ` · ${openPct.toFixed(1)}%`}
                              {r.bounced > 0 && ` · ${r.bounced} bounces`}
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Card>
              <Card title="Blacklist por motivo">
                {metrics.emails.blacklist.by_reason.length === 0 ? (
                  <p className="text-sm text-text-tertiary">Lista vazia.</p>
                ) : (
                  <ul className="flex flex-col gap-3">
                    {metrics.emails.blacklist.by_reason.map((r) => (
                      <li
                        key={r.reason}
                        className="flex items-center justify-between gap-3 text-sm"
                      >
                        <span className="inline-flex items-center gap-2 text-text">
                          <ShieldAlert
                            size={14}
                            strokeWidth={1.75}
                            className="text-text-tertiary"
                            aria-hidden
                          />
                          {BLACKLIST_REASON_LABEL[r.reason] ?? r.reason}
                        </span>
                        <span className="font-medium text-text">{r.n}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </section>
          </div>

          {/* ───────── BREAKDOWN — STATUS + AI KIND ───────── */}
          <section className="grid gap-6 lg:grid-cols-2">
            <Card title="Mix de status">
              {metrics.status_mix.length === 0 ? (
                <p className="text-sm text-text-tertiary">Sem dados.</p>
              ) : (
                <ul className="flex flex-col gap-3">
                  {metrics.status_mix.map((r) => {
                    const pct =
                      statusTotal > 0 ? (r.n / statusTotal) * 100 : 0;
                    return (
                      <li key={r.status} className="flex items-center gap-3">
                        <span className="w-28 text-xs text-text-secondary">
                          {STATUS_LABEL[r.status] ?? r.status}
                        </span>
                        <div className="flex flex-1 items-center gap-3">
                          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-alt">
                            <div
                              className="h-full rounded-full bg-accent"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="w-16 text-right text-xs text-text">
                            {r.n}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>

            <Card title="Custo de IA por tipo">
              {metrics.ai.by_kind.length === 0 ? (
                <p className="text-sm text-text-tertiary">Sem chamadas no período.</p>
              ) : (
                <ul className="flex flex-col gap-3">
                  {metrics.ai.by_kind.map((r) => {
                    const brl =
                      (r.cost_micro_usd / 1_000_000) *
                      metrics.notes.brl_per_usd_assumption;
                    return (
                      <li
                        key={r.kind}
                        className="flex items-baseline justify-between gap-3 text-sm"
                      >
                        <span className="text-text">
                          {r.kind === "summary"
                            ? "Resumo narrativo"
                            : "Avaliação de risco"}
                        </span>
                        <div className="text-right">
                          <p className="font-medium text-text">
                            {formatBRL(brl)}
                          </p>
                          <p className="text-xs text-text-tertiary">
                            {r.calls} chamadas · {r.total_tokens.toLocaleString("pt-BR")} tokens
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>

            <Card title="Custo de IA por modelo">
              {metrics.ai.by_model.length === 0 ? (
                <p className="text-sm text-text-tertiary">Sem chamadas no período.</p>
              ) : (
                <ul className="flex flex-col gap-3">
                  {metrics.ai.by_model.map((r) => {
                    const brl =
                      (r.cost_micro_usd / 1_000_000) *
                      metrics.notes.brl_per_usd_assumption;
                    return (
                      <li
                        key={r.model}
                        className="flex items-baseline justify-between gap-3 text-sm"
                      >
                        <span className="font-mono text-xs text-text">
                          {r.model}
                        </span>
                        <div className="text-right">
                          <p className="font-medium text-text">
                            {formatBRL(brl)}
                          </p>
                          <p className="text-xs text-text-tertiary">
                            {r.calls} chamadas · {r.total_tokens.toLocaleString("pt-BR")} tokens
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </section>

          {/* PER-VERIFICATION TABLE */}
          <section>
            <header className="mb-4 flex items-baseline justify-between">
              <h2 className="text-base font-semibold tracking-tight text-text">
                Últimas verificações
              </h2>
              <p className="text-xs text-text-tertiary">
                {verifs.length} registros · janela {days} d
              </p>
            </header>
            <div className="overflow-x-auto rounded-2xl border border-border bg-surface">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface-alt text-[11px] font-medium uppercase tracking-[0.16em] text-text-tertiary">
                    <th className="px-4 py-3 text-left">Data</th>
                    <th className="px-4 py-3 text-left">Tipo</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-right">Tempo</th>
                    <th className="px-4 py-3 text-right">Tokens</th>
                    <th className="px-4 py-3 text-right">Custo IA (R$)</th>
                    <th className="px-4 py-3 text-right">&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {verifs.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="py-8 text-center text-text-tertiary"
                      >
                        Sem verificações no período.
                      </td>
                    </tr>
                  ) : (
                    verifs.map((v) => (
                      <tr
                        key={v.id}
                        className="border-b border-border last:border-0"
                      >
                        <td className="whitespace-nowrap px-4 py-3 text-text-secondary">
                          {formatDateTime(v.created_at)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-text">
                          {v.target_type.toUpperCase()}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span className="text-xs text-text-secondary">
                            {STATUS_LABEL[v.status] ?? v.status}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-text">
                          {formatDuration(v.duration_seconds)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-text">
                          {v.total_tokens > 0
                            ? v.total_tokens.toLocaleString("pt-BR")
                            : "—"}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-text">
                          {v.cost_micro_usd > 0
                            ? formatBRL(
                                (v.cost_micro_usd / 1_000_000) *
                                  metrics.notes.brl_per_usd_assumption
                              )
                            : "—"}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right">
                          <Link
                            to={`/verifications?id=${v.id}`}
                            className="text-xs font-medium text-accent hover:underline"
                          >
                            Ver
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <p className="text-xs text-text-tertiary">
            BRL estimado usando taxa fixa R$ {metrics.notes.brl_per_usd_assumption}
            /US$. Ajustar em <code>apps/api/src/routes/admin.ts</code> conforme câmbio.
          </p>
        </div>
      ) : null}
    </AdminPage>
  );
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-5">
      <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-text-tertiary">
        {label}
      </p>
      <p className="mt-3 text-xl font-semibold tracking-tight text-text">
        {value}
      </p>
      {hint && <p className="mt-1.5 text-xs text-text-tertiary">{hint}</p>}
    </div>
  );
}

function Card({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-6">
      <h3 className="mb-4 text-sm font-semibold tracking-tight text-text">
        {title}
      </h3>
      {children}
    </div>
  );
}

function SectionHeader({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: typeof Cpu;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="flex items-start gap-3 border-b border-border pb-4">
      <span
        className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-surface-alt text-text-secondary"
        aria-hidden
      >
        <Icon size={16} strokeWidth={1.75} />
      </span>
      <div>
        <h2 className="text-base font-semibold tracking-tight text-text">
          {title}
        </h2>
        <p className="mt-0.5 text-sm text-text-secondary">{subtitle}</p>
      </div>
    </div>
  );
}
